/**
 * Pure utility functions for replay audio decoding, resampling, and mixing.
 * Extracted so they can be unit-tested without the Web Audio API.
 */

/** Decode a base64-encoded PCM16 little-endian buffer to Float32 samples in [-1, 1]. */
export function decodePcm16Base64(base64: string): Float32Array {
  const binary = atob(base64);
  const byteLen = binary.length;
  const sampleCount = Math.floor(byteLen / 2);
  if (sampleCount <= 0) return new Float32Array(0);

  const bytes = new Uint8Array(byteLen);
  for (let i = 0; i < byteLen; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  const view = new DataView(bytes.buffer);
  const out = new Float32Array(sampleCount);
  for (let i = 0; i < sampleCount; i++) {
    out[i] = view.getInt16(i * 2, true) / 32768;
  }
  return out;
}

/**
 * Resample a Float32Array from `srcRate` to `dstRate` using linear interpolation.
 * Good enough for voice audio where the rates are close multiples.
 */
export function linearResample(
  samples: Float32Array,
  srcRate: number,
  dstRate: number,
): Float32Array {
  if (srcRate === dstRate) return samples;
  const ratio = srcRate / dstRate;
  const outLen = Math.ceil(samples.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcPos = i * ratio;
    const idx = Math.floor(srcPos);
    const frac = srcPos - idx;
    const a = samples[idx] ?? 0;
    const b = samples[Math.min(idx + 1, samples.length - 1)] ?? 0;
    out[i] = a + frac * (b - a);
  }
  return out;
}

/** Clamp a value to [-1, 1]. */
function clamp(v: number): number {
  return v > 1 ? 1 : v < -1 ? -1 : v;
}

/**
 * Mix agent and customer frames into a stereo AudioBuffer.
 * Agent biased left (L=0.8, R=0.2), customer biased right (L=0.2, R=0.8).
 * Sum of gains per channel is 1.0, so no clipping under normal conditions.
 * Output is still clamped to [-1, 1] as a safety net.
 * Either input may be null (silence).
 */
export function mixToStereo(
  agentSamples: Float32Array | null,
  customerSamples: Float32Array | null,
  outputRate: number,
  agentSrcRate: number,
  customerSrcRate: number,
): { left: Float32Array; right: Float32Array; length: number } {
  const agent =
    agentSamples && agentSamples.length > 0
      ? linearResample(agentSamples, agentSrcRate, outputRate)
      : null;

  const customer =
    customerSamples && customerSamples.length > 0
      ? linearResample(customerSamples, customerSrcRate, outputRate)
      : null;

  const len = Math.max(agent?.length ?? 0, customer?.length ?? 0);
  if (len === 0) return { left: new Float32Array(0), right: new Float32Array(0), length: 0 };

  const left = new Float32Array(len);
  const right = new Float32Array(len);

  for (let i = 0; i < len; i++) {
    const a = agent && i < agent.length ? agent[i] : 0;
    const c = customer && i < customer.length ? customer[i] : 0;
    left[i] = clamp(a * 0.8 + c * 0.2);
    right[i] = clamp(a * 0.2 + c * 0.8);
  }

  return { left, right, length: len };
}

/**
 * Compute how many silence samples to insert for a sequence gap.
 * Each sequence number represents one 20 ms frame. A gap of N frames
 * means N * frameDurationS seconds of silence at the given output rate.
 *
 * @param gap         Number of missing frames (seq - lastSeq - 1)
 * @param outputRate  AudioContext sample rate (e.g. 48000)
 * @param frameDurationS Duration of one frame in seconds (0.02 = 20 ms)
 * @returns number of silence samples to insert
 */
export function computeGapSilenceSamples(
  gap: number,
  outputRate: number,
  frameDurationS: number,
): number {
  if (gap <= 0) return 0;
  return Math.round(gap * frameDurationS * outputRate);
}
